import {Component, Input, OnInit, OnDestroy, AfterViewInit, HostListener, ChangeDetectorRef, ElementRef, ViewChild} from '@angular/core';
import {ListPageWrapper} from "@app/wrappers/pages/list-page-wrapper/list-page-wrapper";
import {BackButton} from "@components/ui/back-button/back-button";
import {NgForOf, NgIf} from "@angular/common";
import {FormsModule} from "@angular/forms";
import {
  Region,
  RegionMap,
  RegionMapService,
  RegionService,
  Crop,
  CropService
} from "@6grain/masofa-monolith-api-client/dictionaries";
import {TileMapLegend} from "@app/pages/weather/pages/tile-map/tile-map-legend/tile-map-legend";
import {IMsfFormBuilder} from "@components/form-builder/form-builder";
import {ServerTranslateService} from "@services/server-translate.service";
import {ProgressSpinner} from "primeng/progressspinner";
import {TranslateModule, TranslateService} from "@ngx-translate/core";
import {ServerTranslatePipe} from "@app/utils/pipes/server-translate-pipe";
import {Subscription, forkJoin, lastValueFrom} from "rxjs";
import {CommonModule} from "@angular/common";
import {DropdownComponent, DropdownOption} from "@components/dropdown/dropdown.component";
import {DatePickerComponent} from "@components/date-picker/date-picker.component";
import {TileMapDataService, TileMapDataResponse} from "@services/tile-map-data.service";
import {ThemeService} from "@services/theme.service";
import { RegionDictionaryNode} from "@services/region-dictionary.service";
import {ButtonComponent} from "@components/button/button.component";
import {
  PointPolygonDetailsModalComponent
} from '@app/pages/weather/pages/gis-weather/components/point-polygon-details-modal/point-polygon-details-modal.component';
import {RegionManagerService} from '@components/openstreetmap/managers/region-manager.service';
import {MsfTreeSelect} from '@components/tree-select/tree-select';

export interface RegionTile {
  name?: string;
  id?: string;
  mozaikX?: number | null;
  mozaikY?: number | null;
  lat: number | null;
  lng: number | null;
  color?: 'green' | 'yellow' | 'red' | 'blue' | 'gray';
  regionData?: Region; // Добавляем данные региона для перевода
  isSelected?: boolean; // Флаг для выделения выбранных плиток
  tooltip?: string; // Кастомный тултип для плитки
  /** Кэш данных плитки для переформирования тултипа при смене языка */
  cachedTileData?: TileMapDataResponse;
}

export interface RegionData {
  regionId: number;
  totalDistricts: number;
  problemDistricts: number;
  problemPercentage: number;
  alertTypes: string;
}

@Component({
  selector: 'app-title-map',
  imports: [
    ListPageWrapper,
    BackButton,
    NgForOf,
    NgIf,
    TileMapLegend,
    ProgressSpinner,
    TranslateModule,
    ServerTranslatePipe,
    FormsModule,
    CommonModule,
    DropdownComponent,
    DatePickerComponent,
    ButtonComponent,
    PointPolygonDetailsModalComponent,
    MsfTreeSelect
  ],
  templateUrl: './tile-map.html',
  styleUrl: './tile-map.scss',
  standalone: true
})
export class TileMap implements OnInit, AfterViewInit, OnDestroy {

  readonly TILE_SIZE = 35;
  readonly MAP_CONTAINER_PADDING = 80; // 40px * 2

  @ViewChild('mapContainer') mapContainerRef?: ElementRef<HTMLElement>;

  gridWidth: number = 0;
  gridHeight: number = 0;
  /** Масштаб сетки, чтобы карта не выходила за границы контейнера (0..1) */
  gridScale: number = 1;

  tileFilters: IMsfFormBuilder[] = [];
  regions: Region[] = [];
  crops: Crop[] = [];
  cropOptions: DropdownOption[] = [];
  dataTypeOptions: { id: number; name: string }[] = [];
  dataTypeDropdownOptions: DropdownOption[] = [];
  loading: boolean = false;
  loadingRegion: boolean = false;
  loadingCrops: boolean = false;
  hasLoadedOnce: boolean = false; // Флаг, что карта была загружена хотя бы раз
  private languageChangeSubscription?: Subscription;


  // Переменные для фильтрации
  selectedRegion: string | number | null = null;
  selectedParentRegionId: string | null = null;
  selectedRegionId: string | null = null;
  selectedDataType: number | null = null;
  selectedCrop: string | number | null = null;
  /** Источник данных: 0 = ERA5, 1 = IBM */
  dataSourceOptions: DropdownOption[] = [
    { label: 'ERA5', value: 0 },
    { label: 'IBM', value: 1 }
  ];
  filteredTiles: Partial<RegionTile>[] = [];
  filterModel: any = {};

  // Регионы (из ЭКП логики)
  regionTree: RegionDictionaryNode[] = [];
  filteredRegionTree: RegionDictionaryNode[] = [];
  showRegionDropdown = false;
  regionSearch = '';
  selectedRegionLabel = '';
  expandedRegionIds = new Set<string>();
  private regionIndex = new Map<string, RegionDictionaryNode>();

  // Переменные для управления видимостью фильтров и полноэкранного режима
  filtersVisible: boolean = true;
  fullscreenMode: boolean = false;

  // Переменные для центрирования (больше не используются, но оставляем для совместимости)
  isSingleRegionSelected: boolean = false;
  centerOffsetX: number = 0;
  centerOffsetY: number = 0;
  selectedTile:any = {

  }
  selectedTileRegionPath: string | null = null;
  selectedTileAddress: string | null = null;
  showTileWeatherInfos: boolean = false;

  constructor(
    private cropService: CropService,
    private serverTranslate: ServerTranslateService,
    private translateService: TranslateService,
    private tileMapDataService: TileMapDataService,
    private themeService: ThemeService,
    private cdr: ChangeDetectorRef,
    public regionManagementManager: RegionManagerService,
  ) {
    this.selectedRegionLabel = this.getAllRegionsLabel();
  }

  @Input() tiles: Partial<RegionTile>[] = [];
  @Input() regionMap: RegionMap[] = [];
  @Input() regionData: RegionData[] = [];
  @Input() showStatistics: boolean = true;


  ngOnInit() {
    this.calculateGridSize();
    this.initializeDefaultFilters();
    this.initializeFilters();

    // Инициализируем отфильтрованные плитки (будут обновлены после загрузки регионов)
    this.filteredTiles = [...this.tiles];
    // Устанавливаем начальное состояние для всех плиток
    this.filteredTiles.forEach(tile => {
      tile.isSelected = true; // Все плитки обычные по умолчанию
    });

    // Подписываемся на изменения языка
    this.languageChangeSubscription = this.translateService.onLangChange.subscribe(() => {
      this.updateRegionNames();
      this.updateTooltips();
      this.updateFilters();
      this.updateCropOptions();
      this.updateSelectedRegionLabel();
      // Обновляем названия регионов в region-tree
      if (this.regionTree.length > 0) {
        this.filteredRegionTree = this.buildFilteredRegionTree(this.regionSearch);
      }
      this.cdr.markForCheck();
    });

    // Sample data if none provided
    if (this.tiles.length === 0) {
      this.loadSampleData();
    } else {
      // Если плитки уже есть, загружаем данные
      this.loadTileMapData();
    }

    // Загружаем культуры
    this.loadCrops();
  }

  ngAfterViewInit(): void {
    this.updateMapScale();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.updateMapScale();
  }

  /** Масштабирует сетку карты, чтобы она не выходила за границы map-container */
  updateMapScale(): void {
    if (!this.mapContainerRef?.nativeElement || this.gridWidth <= 0 || this.gridHeight <= 0) {
      return;
    }
    const el = this.mapContainerRef.nativeElement;
    const containerW = el.clientWidth - this.MAP_CONTAINER_PADDING;
    const containerH = el.clientHeight - this.MAP_CONTAINER_PADDING;
    const gridW = this.gridWidth * this.TILE_SIZE + (this.gridWidth - 1);
    const gridH = this.gridHeight * this.TILE_SIZE + (this.gridHeight - 1);
    if (containerW <= 0 || containerH <= 0 || gridW <= 0 || gridH <= 0) {
      this.gridScale = 1;
      this.cdr.markForCheck();
      return;
    }
    const scale = Math.min(1, containerW / gridW, containerH / gridH);
    if (this.gridScale !== scale) {
      this.gridScale = scale;
      this.cdr.markForCheck();
    }
  }

  ngOnDestroy() {
    if (this.languageChangeSubscription) {
      this.languageChangeSubscription.unsubscribe();
    }
  }

  private updateRegionNames() {
    // Обновляем имена регионов при смене языка
    for (let tile of this.tiles) {
      if (tile.regionData) {
        tile.name = this.serverTranslate.translate(tile.regionData, 'shortNames') as string;
      }
    }
  }

  /** Переформирует тултипы всех плиток при смене языка (использует cachedTileData). */
  private updateTooltips(): void {
    const tilesToUpdate = this.filteredTiles.length > 0 ? this.filteredTiles : this.tiles;
    for (const tile of tilesToUpdate) {
      tile.tooltip = this.buildWeatherTileTooltip(tile, tile.cachedTileData);
    }
  }

  private updateSelectedRegionLabel(): void {
    // Обновляем label выбранного региона при смене языка
    if (!this.selectedRegionId && !this.selectedParentRegionId) {
      this.selectedRegionLabel = this.getAllRegionsLabel();
    } else if (this.selectedRegionId) {
      const district = this.regionIndex.get(this.selectedRegionId);
      const parent = district?.parentId ? this.regionIndex.get(district.parentId) : null;
      if (parent && district) {
        const parentName = this.getLocalizedRegionName(parent);
        const districtName = this.getLocalizedRegionName(district);
        this.selectedRegionLabel = `${parentName} / ${districtName}`;
      }
    } else if (this.selectedParentRegionId) {
      const region = this.regionIndex.get(this.selectedParentRegionId);
      if (region) {
        this.selectedRegionLabel = this.getLocalizedRegionName(region);
      }
    }
  }

  private updateFilters() {
    // Обновляем фильтры при смене языка
    this.initializeFilters();
  }

  private updateCropOptions(): void {
    this.crops = this.sortCropsAlphabetically([...(this.crops || [])]);
    this.cropOptions = (this.crops || []).map((crop) => ({
      label: this.serverTranslate.translate(crop, 'names') as string,
      value: crop.id
    }));
  }

  private calculateGridSize() {
    // Всегда используем все плитки для расчета размера сетки, а не только отфильтрованные
    const tilesToCalculate = this.tiles;
    if (tilesToCalculate.length === 0) return;

    // Размер сетки всегда фиксированный, основанный на всех плитках
    this.gridWidth = Math.max(...tilesToCalculate.map(t => t.mozaikX as number)) + 3;
    this.gridHeight = Math.max(...tilesToCalculate.map(t => t.mozaikY as number)) + 3;
    this.centerOffsetX = 0;
    this.centerOffsetY = 0;
  }

  private async loadSampleData() {
    this.loading = true;
    try {
      // Используем кешированный метод из RegionManagementManager вместо прямого вызова API
      const data = await this.regionManagementManager.getRegionMaps();
      this.regionMap = data;
      // Не устанавливаем tiles сразу - сначала загрузим регионы и отфильтруем
      // Создаем временный массив тайлов без присваивания в this.tiles
      const allTiles = data.map((regionMap) => {
        return {
          name: '',
          id: regionMap.id,
          mozaikX: regionMap.mozaikX,
          mozaikY: regionMap.mozaikY,
          lat: regionMap.lat,
          lng: regionMap.lng,
          color: 'gray' as const
        }
      });

      // Загружаем регионы с передачей тайлов для последующей фильтрации
      await this.loadRegions(allTiles);
    } catch (error) {
      this.loading = false;
      console.error('Error fetching region map data:', error);
    }
  }

  async loadRegions(allTiles?: Partial<RegionTile>[]): Promise<void> {
    this.loadingRegion = true;

    try {
      // Загружаем регионы через единый кеш RegionManagementManager
      const [regionsTree, regions] = await Promise.all([
        this.regionManagementManager.getRegionTree(),
        this.regionManagementManager.getRegionsByMap()
      ]);
      // Обработка region tree (как в ЭКП)
      const regionNodes = regionsTree.map((region: RegionDictionaryNode) => ({
        ...region,
        expanded: false,
        children: region.children
          ? region.children.map((child: RegionDictionaryNode) => ({
            ...child,
            expanded: false,
            children: child.children ?? []
          }))
          : []
      }));

      const sortedRegionNodes = this.sortRegionsByLocalizedName(regionNodes);

      const allOption: RegionDictionaryNode = {
        id: 'all',
        name: this.getAllRegionsLabel(),
        parentId: null,
        level: 0,
        regionMapId: null,
        expanded: false,
        children: []
      };

      this.regionTree = [allOption, ...sortedRegionNodes];
      this.buildRegionIndex(this.regionTree);
      this.selectedRegionLabel = this.getAllRegionsLabel();
      this.filteredRegionTree = this.buildFilteredRegionTree('');

      // Загружаем регионы с level 2 и 3 для отображения в старом фильтре (временно)
      this.regions = regions.filter((region: Region) => region.level === 2 || region.level === 3);

      // Используем переданные тайлы или существующие
      const tilesToProcess = allTiles || this.tiles || [];

      // Связываем регионы с тайлами
      for (let region of regions) {
        const tile = tilesToProcess.find(t => t.id == region.regionMapId);
        if (tile) {
          // Сохраняем данные региона в плитке для последующего перевода
          tile.regionData = region;
          // Устанавливаем начальное имя
          tile.name = this.serverTranslate.translate(region, 'shortNames') as string;
          // Базовый тултип с названием региона (данные добавятся после загрузки)
          tile.cachedTileData = undefined;
          tile.tooltip = this.buildWeatherTileTooltip(tile, undefined);
        }
      }

      // Показываем плитки и для level 2 (регион), и для level 3 (район) — на плитке есть место
      const filteredTiles = tilesToProcess.filter(tile =>
        tile.regionData && (tile.regionData.level === 2 || tile.regionData.level === 3)
      );

      // Теперь устанавливаем отфильтрованные тайлы
      this.tiles = filteredTiles;

      // Обновляем отфильтрованные плитки
      this.filteredTiles = [...this.tiles];
      // Устанавливаем начальное состояние для всех плиток
      this.filteredTiles.forEach(tile => {
        tile.isSelected = true; // Все плитки обычные по умолчанию
      });

      // Пересчитываем размер сетки после фильтрации и установки тайлов
      this.calculateGridSize();

      // После отрисовки карты снимаем лоадер и отмечаем, что карта была загружена
      this.loading = false;
      this.hasLoadedOnce = true;
      this.loadingRegion = false;
      this.cdr.markForCheck();

      // Масштаб под границы контейнера после отрисовки сетки
      setTimeout(() => this.updateMapScale(), 0);

      // Загружаем данные после загрузки регионов (теперь с лоадером поверх карты)
      this.loadTileMapData();
    } catch (error) {
      console.error('Error fetching region data:', error);
      this.loadingRegion = false;
      this.loading = false;
      this.cdr.markForCheck();
    }
  }

  loadCrops() {
    this.loadingCrops = true;
    this.cropService.dictionariesCropGetByQueryPost({})
      .subscribe({
        next: (data) => {
          // Sort crops alphabetically before assigning
          this.crops = this.sortCropsAlphabetically(data);
          this.updateCropOptions();
          this.loadingCrops = false;
          // Устанавливаем первую культуру по умолчанию после загрузки, если выбран тип 5
          if (this.crops.length > 0 && this.filterModel.DataType === 5 && !this.filterModel.cropId) {
            const firstCropId = this.crops[0]?.id;
            if (firstCropId) {
              this.filterModel.cropId = firstCropId;
              this.selectedCrop = firstCropId;
            }
          }
        },
        error: (error) => {
          console.error('Error fetching crop data:', error);
          this.loadingCrops = false;
        }
      });
  }

  // Инициализация значений по умолчанию
  private initializeDefaultFilters() {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const today = new Date();

    this.filterModel = {
      DataType: 2, // Температурные отклонения по умолчанию
      dataSource: 0, // Era по умолчанию
      startAt: yesterday.toISOString().split('T')[0],
      endAt: today.toISOString().split('T')[0],
      date: today.toISOString().split('T')[0]
    };

    this.selectedDataType = 2;
  }


  initializeFilters() {
    // Подготавливаем типы данных с переводами
    const dataTypesWithTranslations = this.getDataTypeOptionsWithTranslations();
    this.dataTypeOptions = dataTypesWithTranslations;
    this.dataTypeDropdownOptions = this.dataTypeOptions.map(o => ({
      label: o.name,
      value: o.id
    }));

    // Убираем фильтр regionId из msf-filters, так как теперь используем region-selector
    this.tileFilters = [
      {
        name: 'DataType',
        title: 'tile_map.filters.data_type',
        type: 'select',
        selectOptions: dataTypesWithTranslations,
        options: {
          select: {
            optionValue: 'id',
            optionLabel: 'name',
            translateBy: 'none'
          }
        },
        placeholder: 'tile_map.filters.select_data_type'
      },
      {
        name: 'date',
        title: 'Date',
        type: 'date',
        placeholder: 'Select Date'
      },
      {
        name: 'cropId',
        title: 'tile_map.filters.culture',
        type: 'select-paginate',
        selectService: this.cropService,
        selectServiceMethod: 'dictionariesCropGetByQueryPost',
        options: {
          select: {
            searchBy: 'names'
          }
        },
        placeholder: 'tile_map.filters.select_culture'
      },
    ];
  }

  onApplyFilters(): void {
    if (!this.validateFilters()) {
      return;
    }

    // Обновляем выбранные фильтры
    if (this.filterModel.regionId) {
      this.selectedRegion = this.filterModel.regionId;
      this.selectedRegionId = this.filterModel.regionId;
    } else {
      // Используем region-tree если он выбран
      this.selectedRegion = this.selectedRegionId || this.selectedParentRegionId || null;
    }

    this.selectedDataType = this.filterModel.DataType || null;
    this.selectedCrop = this.filterModel.cropId || null;
    this.applyRegionFilter();
    this.loadTileMapData();
  }

  onFilterModelChange(): void {
    if (this.filterModel.DataType !== this.selectedDataType) {
      this.onDataTypeChange();
    }
  }

  // Обработчик изменения типа данных
  onDataTypeChange(): void {
    // Если выбран тип 5 и культура не выбрана, выбираем первую
    if (this.filterModel.DataType === 5 && !this.filterModel.cropId && this.crops.length > 0) {
      const firstCropId = this.crops[0]?.id;
      if (firstCropId) {
        this.filterModel.cropId = firstCropId;
        this.selectedCrop = firstCropId;
      }
    }

    // Если выбран другой тип, сбрасываем культуру
    if (this.filterModel.DataType !== 5) {
      this.filterModel.cropId = null;
      this.selectedCrop = null;
    }
  }

  // Валидация фильтров
  private validateFilters(): boolean {
    // Проверяем, что выбран тип данных
    if (!this.filterModel.DataType) {
      alert(this.translateService.instant('tile_map.validation.select_data_type'));
      return false;
    }

    // Если выбран тип 5, проверяем, что выбрана культура
    if (this.filterModel.DataType === 5 && !this.filterModel.cropId) {
      alert(this.translateService.instant('tile_map.validation.select_culture_for_conditions'));
      return false;
    }

    if (!this.filterModel.date) {
      alert(this.translateService.instant('tile_map.validation.select_date'));
      return false;
    }

    return true;
  }

  onClearFilters(): void {
    // Сбрасываем к значениям по умолчанию
    this.initializeDefaultFilters();

    this.selectedRegion = null;
    this.selectedRegionId = null;
    this.selectedParentRegionId = null;
    this.selectedRegionLabel = '';
    this.selectedCrop = null;
    this.filteredTiles = [...this.tiles];
    // Сбрасываем состояние выбора для всех плиток
    this.filteredTiles.forEach(tile => {
      tile.isSelected = true; // Все плитки обычные
      tile.color = 'gray'; // Сбрасываем цвет к серому
    });
    this.isSingleRegionSelected = false;
    this.centerOffsetX = 0;
    this.centerOffsetY = 0;

    // Загружаем данные с новыми фильтрами
    this.loadTileMapData();
  }

  // Методы для работы с region-tree (из ЭКП)
  private sortRegionsByLocalizedName(regions: RegionDictionaryNode[]): RegionDictionaryNode[] {
    return regions
      .map(region => ({
        ...region,
        children: region.children
          ? this.sortRegionsByLocalizedName(region.children)
          : []
      }))
      .sort((a, b) => {
        const nameA = this.getLocalizedRegionName(a).toLowerCase();
        const nameB = this.getLocalizedRegionName(b).toLowerCase();
        return nameA.localeCompare(nameB, this.translateService.currentLang || 'ru');
      });
  }

  getLocalizedRegionName(node: RegionDictionaryNode | null | undefined): string {
    if (!node) {
      return '';
    }
    const currentLang = this.translateService.currentLang || 'ru-RU';
    const names = node.names || {};
    return (
      names[currentLang] ??
      names[currentLang.split('-')[0]] ??
      node.name ??
      ''
    );
  }

  getAllRegionsLabel(): string {
    return this.translateService.instant('INDEX_TILE_MAP.REGION_ALL') || 'Все регионы';
  }

  getRegionFilterLabel(): string {
    const translated = this.translateService.instant('tile_map.filters.region');
    return translated !== 'tile_map.filters.region' ? translated : 'Region';
  }

  getSelectedRegionLabel(): string {
    if (this.selectedRegionLabel) {
      return this.selectedRegionLabel;
    }
    const translated = this.translateService.instant('tile_map.filters.select_region');
    return translated !== 'tile_map.filters.select_region' ? translated : 'Select Region';
  }

  getRegionSearchPlaceholder(): string {
    const translated = this.translateService.instant('tile_map.filters.search_region');
    return translated !== 'tile_map.filters.search_region' ? translated : 'Search Region';
  }

  getFiltersTitle(): string {
    const translated = this.translateService.instant('FILTERS');
    return translated && translated !== 'FILTERS' ? translated : 'Фильтры';
  }

  getClearRegionTitle(): string {
    const translated = this.translateService.instant('tile_map.filters.clear_region');
    return translated && translated !== 'tile_map.filters.clear_region' ? translated : 'Очистить регион';
  }

  private buildRegionIndex(nodes: RegionDictionaryNode[]): void {
    this.regionIndex.clear();
    const flattened = this.flattenRegionTree(nodes);
    flattened.forEach(node => this.regionIndex.set(node.id, node));
  }

  private flattenRegionTree(nodes: RegionDictionaryNode[]): RegionDictionaryNode[] {
    const stack = [...nodes];
    const result: RegionDictionaryNode[] = [];

    while (stack.length) {
      const node = stack.pop();
      if (!node || node.id === 'all') {
        continue;
      }
      result.push(node);
      if (node.children?.length) {
        stack.push(...node.children);
      }
    }

    return result;
  }

  private buildFilteredRegionTree(searchTerm: string): RegionDictionaryNode[] {
    const term = searchTerm.trim().toLowerCase();
    if (!term) {
      return this.regionTree.map(region => ({
        ...region,
        name: region.id === 'all' ? this.getAllRegionsLabel() : this.getLocalizedRegionName(region),
        children: region.children ? region.children.map(child => ({
          ...child,
          name: this.getLocalizedRegionName(child)
        })) : []
      }));
    }

    const filtered: RegionDictionaryNode[] = [];

    this.regionTree.forEach(region => {
      const localizedRegionName = region.id === 'all' ? this.getAllRegionsLabel() : this.getLocalizedRegionName(region);

      if (region.id === 'all') {
        if (localizedRegionName.toLowerCase().includes(term)) {
          filtered.push({...region, name: localizedRegionName, children: []});
        }
        return;
      }

      const matchesRegion = localizedRegionName.toLowerCase().includes(term);
      const matchingChildren =
        region.children
          ?.filter(child => {
            const localizedChildName = this.getLocalizedRegionName(child);
            return localizedChildName.toLowerCase().includes(term);
          })
          .map(child => ({
            ...child,
            name: this.getLocalizedRegionName(child)
          })) ?? [];

      if (matchesRegion || matchingChildren.length) {
        filtered.push({
          ...region,
          name: localizedRegionName,
          expanded: matchesRegion || matchingChildren.length > 0,
          children: matchesRegion
            ? (region.children ? region.children.map(child => ({
              ...child,
              name: this.getLocalizedRegionName(child)
            })) : [])
            : matchingChildren
        });
      }
    });

    return filtered;
  }

  toggleRegionDropdown(): void {
    this.showRegionDropdown = !this.showRegionDropdown;
  }

  onRegionSearchChange(): void {
    this.filteredRegionTree = this.buildFilteredRegionTree(this.regionSearch);
  }

  toggleRegionExpansion(region: RegionDictionaryNode, event?: Event): void {
    event?.stopPropagation();
    if (this.expandedRegionIds.has(region.id)) {
      this.expandedRegionIds.delete(region.id);
      region.expanded = false;
    } else {
      this.expandedRegionIds.add(region.id);
      region.expanded = true;
    }
  }

  selectRegion(region: RegionDictionaryNode, closeDropdown: boolean = true): void {
    if (region.id === 'all') {
      this.selectedRegionLabel = this.getAllRegionsLabel();
      this.selectedParentRegionId = null;
      this.selectedRegionId = null;
      this.selectedRegion = null;
      if (closeDropdown) {
        this.showRegionDropdown = false;
      }
      this.applyRegionFilter();
      return;
    }

    this.selectedRegionLabel = this.getLocalizedRegionName(region);
    this.selectedParentRegionId = region.id;
    this.selectedRegionId = null;
    this.selectedRegion = region.id;
    if (closeDropdown) {
      this.showRegionDropdown = false;
    }
    this.applyRegionFilter();
  }

  selectDistrict(parent: RegionDictionaryNode, district: RegionDictionaryNode, event?: Event): void {
    event?.stopPropagation();
    const parentName = this.getLocalizedRegionName(parent);
    const districtName = this.getLocalizedRegionName(district);
    this.selectedRegionLabel = `${parentName} / ${districtName}`;
    this.selectedParentRegionId = parent.id;
    this.selectedRegionId = district.id;
    this.selectedRegion = district.id;
    this.showRegionDropdown = false;
    this.applyRegionFilter();
  }

  clearRegionFilter(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.selectedRegion = null;
    this.selectedParentRegionId = null;
    this.selectedRegionId = null;
    this.selectedRegionLabel = this.getAllRegionsLabel();
    this.applyRegionFilter();
  }

  @HostListener('document:click', ['$event'])
  handleDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.region-selector')) {
      if (this.showRegionDropdown) {
        this.showRegionDropdown = false;
        this.cdr.markForCheck();
      }
    }
  }

  private applyRegionFilter(): void {
    if (!this.selectedRegion && !this.selectedParentRegionId) {
      // No region selected — show all tiles as active
      this.filteredTiles = [...this.tiles];
      this.filteredTiles.forEach(tile => {
        tile.isSelected = true;
      });
      this.isSingleRegionSelected = false;
    } else {
      // Use regionIndex (built from tree, string IDs) to avoid type-mismatch with this.regions
      let selectedLevel: number | null = null;
      let targetRegionId: string | null = null;

      if (this.selectedRegionId) {
        // A district was selected — look it up in the regionIndex
        const node = this.regionIndex.get(this.selectedRegionId);
        const level = node?.level ?? (node?.parentId ? 3 : 2);
        selectedLevel = level;
        targetRegionId = this.selectedRegionId;
      } else if (this.selectedParentRegionId) {
        // A parent region was selected — look it up in the regionIndex
        const node = this.regionIndex.get(this.selectedParentRegionId);
        const level = node?.level ?? (node?.parentId ? 3 : 2);
        selectedLevel = level;
        targetRegionId = this.selectedParentRegionId;
      }

      // Always show all tiles; mark selected ones
      this.filteredTiles = [...this.tiles];

      if (selectedLevel !== null && selectedLevel <= 2) {
        // Parent region selected — highlight the region tile itself AND all its district children
        this.filteredTiles.forEach(tile => {
          const tileRegionId = tile.regionData?.id?.toString();
          const tileParentId = tile.regionData?.parentId?.toString();
          if (
            tileRegionId === targetRegionId ||
            tileParentId === targetRegionId
          ) {
            tile.isSelected = true;
          } else {
            tile.isSelected = false;
          }
        });
      } else if (selectedLevel !== null && selectedLevel > 2) {
        // District selected — highlight only that single tile
        this.filteredTiles.forEach(tile => {
          tile.isSelected = tile.regionData?.id?.toString() === targetRegionId;
        });
      } else {
        // Fallback
        this.filteredTiles.forEach(tile => {
          const tileId = tile.regionData?.id?.toString();
          tile.isSelected =
            tileId === this.selectedRegionId ||
            tileId === this.selectedRegion?.toString();
        });
      }

      this.isSingleRegionSelected = false;
    }
    this.updateDisplayTiles();
  }

  private updateDisplayTiles(): void {
    // Обновляем отображаемые плитки (размер сетки не изменяется)
    // this.calculateGridSize(); // Убираем, так как размер сетки фиксированный
  }

  private loadTileMapData(): void {
    if (!this.selectedDataType) {
      console.log('⚠️ No data type selected, clearing tile colors');
      this.filteredTiles.forEach(tile => tile.color = 'gray');
      return;
    }

    if (this.filteredTiles.length === 0) {
      console.log('⚠️ No tiles to load data for');
      return;
    }

    // Показываем лоадер поверх карты пока грузятся данные
    this.loading = true;

    // Reset colors and тултипы на "нет данных" пока грузим
    this.filteredTiles.forEach(tile => {
      tile.color = 'gray';
      tile.cachedTileData = undefined;
      tile.tooltip = this.buildWeatherTileTooltip(tile, undefined);
    });

    // Получаем список ID регионов для batch запроса
    const regionIds = this.filteredTiles
      .filter(tile => tile.id)
      .map(tile => tile.id!);

    if (regionIds.length === 0) {
      console.log('⚠️ No valid region IDs found');
      return;
    }

    console.log(`📊 Loading tile map data for ${regionIds.length} tiles in batch, dataType: ${this.selectedDataType}`);

    // Загружаем данные для всех регионов одним запросом
    const dataSource = this.filterModel.dataSource !== undefined && this.filterModel.dataSource !== null
      ? Number(this.filterModel.dataSource) : 0;
    this.tileMapDataService.getTileMapDataBatch({
      regionMapIds: regionIds,
      dataType: this.selectedDataType!,
      dataSource,
      cropId: this.selectedCrop ? String(this.selectedCrop) : undefined,
      startDate: this.filterModel.startAt,
      endDate: this.filterModel.endAt,
      date: this.filterModel.date
    }).subscribe({
      next: (dataArray: TileMapDataResponse[]) => {
        console.log(`✅ Batch data loaded: ${dataArray.length} regions`);

        // Создаем Map для быстрого поиска данных по regionId
        const dataMap = new Map<string, TileMapDataResponse>();
        dataArray.forEach(data => {
          dataMap.set(data.regionId, data);
        });

        // Обновляем цвета и тултипы для всех плиток (сохраняем data для переформирования при смене языка)
        this.filteredTiles.forEach(tile => {
          if (tile.id) {
            const data = dataMap.get(tile.id);
            if (data) {
              // Пропускаем данные с техническими ошибками - обрабатываем как отсутствие данных
              if (data.message && this.isTechnicalError(data.message)) {
                console.warn(`⚠️ Technical error for region ${tile.id}: ${data.message}`);
                tile.color = 'gray';
                tile.cachedTileData = undefined;
                tile.tooltip = this.buildWeatherTileTooltip(tile, undefined);
              } else {
                this.updateTileColorFromData(tile, data);
                tile.cachedTileData = data;
                tile.tooltip = this.buildWeatherTileTooltip(tile, data);
              }
            } else {
              console.warn(`⚠️ No data found for region ${tile.id}`);
              tile.color = 'gray';
              tile.cachedTileData = undefined;
              tile.tooltip = this.buildWeatherTileTooltip(tile, undefined);
            }
          }
        });

        this.loading = false;
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error(`❌ Error loading batch tile map data, dataType ${this.selectedDataType}:`, error);
        // Устанавливаем серый цвет и тултип "нет данных" для всех плиток при ошибке
        this.filteredTiles.forEach(tile => {
          tile.color = 'gray';
          tile.cachedTileData = undefined;
          tile.tooltip = this.buildWeatherTileTooltip(tile, undefined);
        });
        this.loading = false;
        this.cdr.markForCheck();
      }
    });
  }

  private updateTileColorFromData(tile: Partial<RegionTile>, data: TileMapDataResponse): void {
    if (!tile) return;
    const color = this.normalizeTileColor(data?.color);
    tile.color = color;
    if (color === 'gray') {
      console.warn(`⚠️ Tile ${tile.id} set to gray – no data received`);
    }
  }

  /**
   * Проверяет, является ли сообщение технической ошибкой, которую не нужно показывать пользователю
   */
  private isTechnicalError(message: string): boolean {
    if (!message) return false;
    const lowerMessage = message.toLowerCase();
    // Проверяем на технические ошибки базы данных и другие системные ошибки
    return (
      lowerMessage.includes('error:') ||
      lowerMessage.includes('st_distance') ||
      lowerMessage.includes('srid') ||
      lowerMessage.includes('operation on mixed') ||
      lowerMessage.includes('xx000:') ||
      lowerMessage.includes('exception') ||
      lowerMessage.startsWith('error loading data:')
    );
  }

  /**
   * Формирует тултип для плитки погоды: регион + данные/сообщение, без технических номеров
   */
  private buildWeatherTileTooltip(tile: Partial<RegionTile>, data: TileMapDataResponse | undefined): string {
    const lines: string[] = [];

    // Название региона (полное) на нужном языке
    if (tile.regionData) {
      const fullName = this.serverTranslate.translate(tile.regionData, 'names') as string;
      if (fullName) {
        lines.push(fullName);
      }
    } else if (tile.name) {
      lines.push(tile.name);
    }

    // Пользовательское сообщение от бекенда, если есть и это не техническая ошибка
    if (data?.message && !this.isTechnicalError(data.message)) {
      const message = data.message.trim();
      // Ключ для перевода: бэкенд присылает snake_case (no_stations) или старый текст — нормализуем для поиска
      const keySuffix = message.includes(' ') ? message.toLowerCase().replace(/\s+/g, '_') : message;
      const messageKey = `tile_map.messages.${keySuffix}`;
      const translated = this.translateService.instant(messageKey);
      if (translated !== messageKey) {
        lines.push(translated);
      } else {
        // Обратная совместимость: старые текстовые сообщения от бэкенда
        const lowerMessage = message.toLowerCase();
        if (lowerMessage === 'no stations') {
          lines.push(this.translateService.instant('tile_map.messages.no_stations'));
        } else if (lowerMessage.includes('no weather data') || lowerMessage.includes('no weather data available') ||
            lowerMessage.includes('weather data available on this date')) {
          lines.push(this.translateService.instant('tile_map.messages.no_weather_data'));
        } else if (lowerMessage.includes('date is required for frost data') ||
                   lowerMessage.includes('date is required')) {
          lines.push(this.translateService.instant('tile_map.messages.date_required'));
        } else if (lowerMessage.includes('date and crop') ||
                   lowerMessage.includes('cropid are required') ||
                   lowerMessage.includes('date and cropid are required')) {
          lines.push(this.translateService.instant('tile_map.messages.crop_id_required'));
        } else if (lowerMessage.includes('unknown data type')) {
          const localizedMessage = this.translateService.instant('TILE_MAP.MESSAGES.UNKNOWN_DATA_TYPE');
          lines.push(localizedMessage);
        } else {
          const errorPrefix = this.translateService.instant('TILE_MAP.MESSAGES.ERROR');
          if (lowerMessage.startsWith('error:')) {
            lines.push(`${errorPrefix}: ${message.slice(6).trim()}`);
          } else {
            lines.push(message);
          }
        }
      }
    } else if (data?.data != null) {
      // Если в data лежит простое значение, показываем его
      if (typeof data.data === 'number' || typeof data.data === 'string') {
        lines.push(String(data.data));
      } else if (typeof data.data === 'object') {
        // Проверяем тип данных по структуре объекта
        const dataObj = data.data as any;

        // Проверяем, является ли это объектом с полями actual, average, deviation (температурные/осадочные отклонения)
        if (dataObj.actual !== undefined || dataObj.average !== undefined || dataObj.deviation !== undefined) {
          // Форматируем с переведенными ключами, используя то же форматирование, что и в модалке
          const formattedParts: string[] = [];

          // Вспомогательная функция для форматирования чисел (как в модалке)
          const formatNumber = (value: number | null | undefined, decimals: number = 1, isTemperature: boolean = false): string => {
            if (value === null || value === undefined) return '—';
            const rounded = Number(value.toFixed(decimals));
            if (isTemperature && rounded > 0) {
              return `+${rounded.toFixed(decimals)}`;
            }
            return rounded.toFixed(decimals);
          };

          if (dataObj.actual !== undefined && dataObj.actual !== null) {
            const actualLabel = this.translateService.instant('TILE_MAP.DATA.ACTUAL');
            // Определяем тип данных по DataType для правильного форматирования
            const isTemp = this.selectedDataType === 2; // DataType 2 - температурные отклонения
            const isPrecip = this.selectedDataType === 3; // DataType 3 - осадочные отклонения
            const formattedValue = formatNumber(Number(dataObj.actual), 1, isTemp);
            const unit = isTemp ? '°C' : (isPrecip ? ' мм' : '');
            formattedParts.push(`${actualLabel}: ${formattedValue}${unit}`);
          }

          if (dataObj.average !== undefined && dataObj.average !== null) {
            const averageLabel = this.translateService.instant('TILE_MAP.DATA.AVERAGE') || 'Среднее';
            const isTemp = this.selectedDataType === 2;
            const isPrecip = this.selectedDataType === 3;
            const formattedValue = formatNumber(Number(dataObj.average), 1, isTemp);
            const unit = isTemp ? '°C' : (isPrecip ? ' мм' : '');
            formattedParts.push(`${averageLabel}: ${formattedValue}${unit}`);
          }

          if (dataObj.deviation !== undefined && dataObj.deviation !== null) {
            const deviationLabel = this.translateService.instant('TILE_MAP.DATA.DEVIATION');
            const isTemp = this.selectedDataType === 2;
            const isPrecip = this.selectedDataType === 3;
            const formattedValue = formatNumber(Number(dataObj.deviation), 1, isTemp);
            const unit = isTemp ? '°C' : (isPrecip ? ' мм' : '');
            formattedParts.push(`${deviationLabel}: ${formattedValue}${unit}`);
          }

          if (formattedParts.length > 0) {
            lines.push(formattedParts.join(', '));
          } else {
            // Если все значения undefined/null, показываем как нет данных
            const noData = this.translateService.instant('TILE_MAP.NO_WEATHER_DATA_AVAILABLE') ||
                           this.translateService.instant('TILE_MAP.NO_DATA') ||
                           this.translateService.instant('NO_WEATHER_DATA_AVAILABLE') ||
                           this.translateService.instant('INDEX_TILE_MAP.TOOLTIP.NO_DATA');
            lines.push(noData);
          }
        }
        // Проверяем, является ли это объектом с полями minTemperature, hasFrost (данные о заморозках)
        else if (dataObj.minTemperature !== undefined || dataObj.hasFrost !== undefined) {
          const formattedParts: string[] = [];

          if (dataObj.minTemperature !== undefined && dataObj.minTemperature !== null) {
            const minTempLabel = this.translateService.instant('TILE_MAP.DATA.MIN_TEMPERATURE');
            // Форматируем температуру как в модалке: добавляем плюс для положительных, округление до 1 знака
            const tempValue = Number(dataObj.minTemperature);
            const formattedTemp = tempValue > 0 ? `+${tempValue.toFixed(1)}` : tempValue.toFixed(1);
            formattedParts.push(`${minTempLabel}: ${formattedTemp}°C`);
          }

          if (dataObj.hasFrost !== undefined && dataObj.hasFrost !== null) {
            if (dataObj.hasFrost === true || dataObj.hasFrost === 'true') {
              const frostLabel = this.translateService.instant('TILE_MAP.DATA.HAS_FROST');
              formattedParts.push(frostLabel);
            } else {
              const noFrostLabel = this.translateService.instant('TILE_MAP.DATA.NO_FROST');
              formattedParts.push(noFrostLabel);
            }
          }

          if (formattedParts.length > 0) {
            lines.push(formattedParts.join(', '));
            // Добавляем информацию о пороге заморозков
            const frostThresholdNote = this.translateService.instant('TILE_MAP.FROST_THRESHOLD_NOTE');
            lines.push(frostThresholdNote);
          } else {
            const noData = this.translateService.instant('TILE_MAP.NO_WEATHER_DATA_AVAILABLE') ||
                           this.translateService.instant('TILE_MAP.NO_DATA') ||
                           this.translateService.instant('NO_WEATHER_DATA_AVAILABLE');
            lines.push(noData);
          }
        } else {
          // Для других структур – короткий JSON
          try {
            const json = JSON.stringify(data.data);
            lines.push(json.length > 120 ? json.slice(0, 117) + '...' : json);
          } catch {
            lines.push('');
          }
        }
      }
    } else {
      // Нет данных
      const noData = this.translateService.instant('TILE_MAP.NO_WEATHER_DATA_AVAILABLE') ||
                     this.translateService.instant('TILE_MAP.NO_DATA') ||
                     this.translateService.instant('NO_WEATHER_DATA_AVAILABLE') ||
                     this.translateService.instant('INDEX_TILE_MAP.TOOLTIP.NO_DATA');
      lines.push(noData);
    }

    return lines.filter(Boolean).join('\n');
  }

  private normalizeTileColor(color?: string): 'green' | 'yellow' | 'red' | 'blue' | 'gray' {
    const allowedColors = ['green', 'yellow', 'red', 'blue', 'gray'];
    return allowedColors.includes(color || '') ? (color as 'green' | 'yellow' | 'red' | 'blue' | 'gray') : 'gray';
  }

  // Геттер для получения отображаемых плиток
  get displayTiles(): Partial<RegionTile>[] {
    // Всегда показываем все плитки, выделение происходит через CSS классы (isSelected)
    // Логика выделения обрабатывается в applyRegionFilter() и getTileClasses()
    return this.filteredTiles.length > 0 ? this.filteredTiles : this.tiles;
  }

  // Геттер для проверки темной темы
  get isDarkTheme(): boolean {
    return this.themeService.isDarkMode();
  }

  // Геттер для получения отфильтрованных фильтров (скрывает фильтр культуры если не выбран тип 5)
  // Фильтр региона убран, так как используется region-selector
  get filteredTileFilters(): IMsfFormBuilder[] {
    return this.tileFilters
      .filter(filter => filter.name !== 'regionId') // Убираем фильтр региона
      .map(filter => {
        // Показываем фильтр культуры только если выбран тип данных 5
        if (filter.name === 'cropId') {
          return {
            ...filter,
            visible: this.filterModel.DataType === 5
          } as IMsfFormBuilder;
        }

        return filter;
      })
      .filter(filter => {
        // Скрываем фильтр культуры если не выбран тип 5
        if (filter.name === 'cropId') {
          return this.filterModel.DataType === 5;
        }
        return true;
      });
  }

  // Метод для получения отформатированных опций типов данных (аналогично регионам)
  private getDataTypeOptionsWithTranslations() {
    return [
      {id: 2, name: this.translateService.instant('tile_map.data_types.temperature_deviations')},
      {id: 3, name: this.translateService.instant('tile_map.data_types.precipitation_deviations')},
      {id: 4, name: this.translateService.instant('tile_map.data_types.frost_map')},
      {id: 5, name: this.translateService.instant('tile_map.data_types.unfavorable_conditions')}
    ];
  }

  // Методы для управления интерфейсом
  toggleFilters(): void {
    this.filtersVisible = !this.filtersVisible;
    setTimeout(() => this.updateMapScale(), 100); // пересчёт масштаба после изменения ширины зоны карты
  }

  // toggleFullscreen(): void {
  //   this.fullscreenMode = !this.fullscreenMode;
  // }

  // Метод для получения отформатированного названия региона
  private getRegionDisplayName(region: Region): string {
    const name = this.serverTranslate.translate(region, 'shortNames') as string;
    const code = region.nameMhobt ? ` (${region.nameMhobt})` : '';
    return `${name}${code}`;
  }

  // Метод для получения полного названия региона для tooltip
  getRegionTooltip(region: Region | undefined): string {
    if (!region) return '';

    const fullName = this.serverTranslate.translate(region, 'names') as string;
    const code = region.nameMhobt ? ` (${region.nameMhobt})` : '';
    return `${fullName}${code}`;
  }

  // Метод для получения CSS классов для плитки с учетом позиции tooltip
  getTileClasses(tile: Partial<RegionTile>): string {
    const baseClasses = [
      'tile',
      tile.color || 'gray',
      tile.isSelected !== false ? 'selected' : '',
      this.selectedRegion && tile.isSelected === false ? 'dimmed' : '',
      this.isDarkTheme ? 'dark-theme' : 'light-theme'
    ].filter(Boolean);

    // Определяем, находится ли плитка в верхней части карты
    const isTopTile = this.isTopTile(tile);
    if (isTopTile) {
      baseClasses.push('top-tile');
    }

    return baseClasses.join(' ');
  }

  // Метод для определения, находится ли плитка в верхней части
  private isTopTile(tile: Partial<RegionTile>): boolean {
    if (tile.mozaikY === null || tile.mozaikY === undefined || this.gridHeight === 0) return false;

    // Считаем плитку верхней, если она находится в первых двух строках
    // Это более консервативный подход для гарантии
    return tile.mozaikY <= 5;
  }

  // Метод для получения заголовка в полноэкранном режиме
  getFullscreenTitle(): string {
    let title = '';

    // Добавляем тип данных
    const dataTypeName = this.selectedDataType
      ? this.getDataTypeName(this.selectedDataType)
      : this.translateService.instant('tile_map.data_types.temperature_deviations');
    title += dataTypeName;

    if (this.filterModel.date) {
      const date = this.formatDateForDisplay(this.filterModel.date);
      title += ` (${date})`;
    }

    // Добавляем культуру если выбран тип 5
    if (this.selectedDataType === 5 && this.selectedCrop) {
      const selectedCropData = this.crops.find(crop => crop.id === this.selectedCrop);
      if (selectedCropData) {
        const cropName = this.serverTranslate.translate(selectedCropData, 'names') as string;
        title += ` - ${cropName}`;
      }
    }

    return title;
  }

  // Метод для получения элементов легенды в зависимости от типа данных
  getLegendItems(): Array<{ color: 'green' | 'yellow' | 'red' | 'blue' | 'gray', label: string }> {
    if (!this.selectedDataType) {
      // По умолчанию - УГМ предупреждения
      return [
        {color: 'green' as const, label: 'tile_map.legend.no_alerts'},
        {color: 'yellow' as const, label: 'tile_map.legend.one_alert'},
        {color: 'red' as const, label: 'tile_map.legend.multiple_alerts'},
        {color: 'gray' as const, label: 'tile_map.legend.no_data'}
      ];
    }

    switch (this.selectedDataType) {
      case 1: // Уведомления от УГМ о стихийных бедствиях
        return [
          {color: 'green' as const, label: 'tile_map.legend.no_alerts'},
          {color: 'yellow' as const, label: 'tile_map.legend.one_alert'},
          {color: 'red' as const, label: 'tile_map.legend.multiple_alerts'},
          {color: 'gray' as const, label: 'tile_map.legend.no_data'}
        ];

      case 2: // Отклонения от температурных норм
        return [
          {color: 'green' as const, label: 'tile_map.legend.temperature_normal'},
          {color: 'red' as const, label: 'tile_map.legend.temperature_above'},
          {color: 'blue' as const, label: 'tile_map.legend.temperature_below'},
          {color: 'gray' as const, label: 'tile_map.legend.no_data'}
        ];

      case 3: // Отклонения от осадочных норм
        return [
          {color: 'green' as const, label: 'tile_map.legend.precipitation_normal'},
          {color: 'red' as const, label: 'tile_map.legend.precipitation_above'},
          {color: 'blue' as const, label: 'tile_map.legend.precipitation_below'},
          {color: 'gray' as const, label: 'tile_map.legend.no_data'}
        ];

      case 4: // Карта заморозков
        return [
          {color: 'green' as const, label: 'tile_map.legend.frost_normal'},
          {color: 'blue' as const, label: 'tile_map.legend.frost_risk'},
          {color: 'gray' as const, label: 'tile_map.legend.no_data'}
        ];

      case 5: // Карта неблагоприятных условий для культур
        return [
          {color: 'green' as const, label: 'tile_map.legend.conditions_normal'},
          {color: 'red' as const, label: 'tile_map.legend.conditions_above'},
          {color: 'blue' as const, label: 'tile_map.legend.conditions_below'},
          {color: 'gray' as const, label: 'tile_map.legend.no_data'}
        ];

      default:
        return [
          {color: 'green' as const, label: 'tile_map.legend.no_alerts'},
          {color: 'yellow' as const, label: 'tile_map.legend.one_alert'},
          {color: 'red' as const, label: 'tile_map.legend.multiple_alerts'},
          {color: 'gray' as const, label: 'tile_map.legend.no_data'}
        ];
    }
  }

  // Метод для получения названия типа данных
  private getDataTypeName(dataType: number): string {
    switch (dataType) {
      case 2:
        return this.translateService.instant('tile_map.data_types.temperature_deviations');
      case 3:
        return this.translateService.instant('tile_map.data_types.precipitation_deviations');
      case 4:
        return this.translateService.instant('tile_map.data_types.frost_map');
      case 5:
        return this.translateService.instant('tile_map.data_types.unfavorable_conditions');
      default:
        return '';
    }
  }

  // Метод для форматирования даты для отображения (день.месяц.год)
  private formatDateForDisplay(dateString: string): string {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  }

  // Обработчик клавиши ESC для выхода из полноэкранного режима
  // @HostListener('document:keydown.escape', ['$event'])
  // onEscapeKey(event: Event): void {
  //   if (this.fullscreenMode) {
  //     this.toggleFullscreen();
  //     event.preventDefault();
  //   }
  // }

  /**
   * Sorts crops alphabetically based on the current language
   * Supports proper collation for Russian, English, and Uzbek
   */
  private sortCropsAlphabetically(crops: Crop[]): Crop[] {
    if (!crops || crops.length === 0) {
      return crops;
    }

    // Get current language from translate service
    const currentLang = this.translateService.currentLang || 'ru';

    // Determine the locale for proper sorting
    let locale = 'ru-RU'; // Default to Russian

    if (currentLang === 'en' || currentLang === 'en-us' || currentLang === 'en-US') {
      locale = 'en-US';
    } else if (currentLang === 'uz' || currentLang === 'uz-latn' || currentLang === 'uz-Latn-UZ') {
      locale = 'uz-Latn-UZ';
    } else if (currentLang === 'ru' || currentLang === 'ru-ru' || currentLang === 'ru-RU') {
      locale = 'ru-RU';
    }

    return crops.sort((a, b) => {
      // Get the translated names for comparison using type assertion for dynamic properties
      const cropA = a as any;
      const cropB = b as any;
      let nameA = '';
      let nameB = '';

      // Get names based on current language
      if (currentLang === 'en' || currentLang === 'en-us' || currentLang === 'en-US') {
        nameA = cropA.nameEn || cropA.nameRu || cropA.nameUz || cropA.name || '';
        nameB = cropB.nameEn || cropB.nameRu || cropB.nameUz || cropB.name || '';
      } else if (currentLang === 'uz' || currentLang === 'uz-latn' || currentLang === 'uz-Latn-UZ') {
        nameA = cropA.nameUz || cropA.nameRu || cropA.nameEn || cropA.name || '';
        nameB = cropB.nameUz || cropB.nameRu || cropB.nameEn || cropB.name || '';
      } else {
        nameA = cropA.nameRu || cropA.nameEn || cropA.nameUz || cropA.name || '';
        nameB = cropB.nameRu || cropB.nameEn || cropB.nameUz || cropB.name || '';
      }

      // Use localeCompare for proper alphabetical sorting with language-specific rules
      return nameA.localeCompare(nameB, locale, {
        sensitivity: 'base', // Ignore case and accents for comparison
        numeric: true,       // Handle numbers properly (e.g., "Crop 2" comes before "Crop 10")
        ignorePunctuation: true // Ignore punctuation for sorting
      });
    });
  }

  async showWeatherInfos(tile: Partial<RegionTile>) {
    this.selectedTile = tile as any;
    this.showTileWeatherInfos = true;
    this.selectedTileRegionPath = await this.regionManagementManager.getRegionPathStringById(tile?.regionData?.id as string)
  }


}
